/**
 * App.tsx — composition root.
 * Providers wrap a surface-based root navigator. SK Queue surfaces are real,
 * separate entry points (no single NavigationContainer/tab god-view), so the
 * SurfaceRouter decides which surface is active and whether it's public
 * (standalone) or admin (shell). The in-memory QueueProvider holds all domain
 * state + the deterministic engine.
 */

import React from 'react';

import { ThemeProvider } from './src/context/themeContext';
import { SessionProvider } from './src/context/sessionContext';
import { QueueProvider } from './src/queue/store';
import { DataSourceProvider } from './src/queue/dataSource';
import { ActiveQueueProvider } from './src/queue/activeQueue';
import { GuestSessionProvider } from './src/queue/guestSession';
import { SurfaceRouterProvider } from './src/navigation/surfaceRouter';
import GlobalErrorBoundary from './src/components/globalErrorBoundary';
import RootNavigator from './src/navigation/rootNavigator';

export default function App() {
  return (
    // Outermost: catch any render/runtime crash and show the fallback instead
    // of a white screen.
    <GlobalErrorBoundary>
      <ThemeProvider>
        <SessionProvider>
          {/* Probes sk-queue-api reachability; UI reads the in-memory store
              until live read/write wiring lands. */}
          <DataSourceProvider>
            {/* Tracks the active queue for live surfaces (lists GET /v1/queues
                in live mode; inert in memory mode). */}
            <ActiveQueueProvider>
              {/* Public guest/door bootstrap: exchanges ?org&q for a guest token.
                  Inert unless the URL carries them, so admin surfaces are unaffected. */}
              <GuestSessionProvider>
                <QueueProvider>
                  <SurfaceRouterProvider>
                    <RootNavigator />
                  </SurfaceRouterProvider>
                </QueueProvider>
              </GuestSessionProvider>
            </ActiveQueueProvider>
          </DataSourceProvider>
        </SessionProvider>
      </ThemeProvider>
    </GlobalErrorBoundary>
  );
}
